import React, { useState, useRef } from 'react';
import ApiClient from '../../../methods/api/apiClient';
import loader from '../../../methods/loader';
import { toast } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';
import { login_success } from '../../../Pages/actions/user';
import environment from '../../../environment';

interface OnboardingWizardProps {
    onClose: () => void;
}

// Preset industries/verticals the LLM tries to match into (users can also add their own via "Other")
const INDUSTRY_OPTIONS = [
    'Digital Health', 'Distribution', 'e-commerce', 'Engineering', 'Entertainment',
    'Finance', 'Fintech', 'Hardware', 'Healthcare', 'Legal', 'Logistics',
    'Manufacturing', 'Medtech', 'Networking', 'Professional Services', 'Restaurant',
    'SaaS', 'Security', 'Semiconductor', 'Software', 'Solartech', 'Supply Chain',
    'Travel', 'Wholesale',
];

const OnboardingWizard: React.FC<OnboardingWizardProps> = ({ onClose }) => {
    const user = useSelector((state: any) => state.user);
    const dispatch = useDispatch();
    const [step, setStep] = useState(1);
    const [websiteOpen, setWebsiteOpen] = useState(false);
    const [linkedInOpen, setLinkedInOpen] = useState(false);
    const [pdfOpen, setPdfOpen] = useState(false);
    const [websiteUrls, setWebsiteUrls] = useState('');
    const [linkedInText, setLinkedInText] = useState('');
    const [importedLinkedIn, setImportedLinkedIn] = useState('');
    const [crawledText, setCrawledText] = useState('');
    const [sources, setSources] = useState<string[]>([]);
    const [isCrawling, setIsCrawling] = useState(false);
    const [isParsing, setIsParsing] = useState(false);
    const [pdfTexts, setPdfTexts] = useState<{name: string, text: string}[]>([]);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const [isGenerating, setIsGenerating] = useState(false);
    const [form, setForm] = useState({
        headline: user.profileData?.headline || '',
        industry: user.profileData?.industry || '',
        specialties: (user.profileData?.specialties || []).join(', '),
        servicesOffered: (user.profileData?.servicesOffered || []).join(', '),
        servicesNeeded: (user.profileData?.servicesNeeded || []).join(', '),
        referralFee: user.profileData?.referralFee || '',
        promotions: user.profileData?.promotions || '',
        websiteUrl: user.profileData?.websiteUrl || '',
        yearsExperience: user.profileData?.yearsExperience || '',
        businessType: user.profileData?.businessType || '',
        businessTypeOther: user.profileData?.businessTypeOther || '',
        industriesServed: (user.profileData?.industriesServed || []).filter((x: string) => INDUSTRY_OPTIONS.includes(x)) as string[],
        industryOther: (user.profileData?.industriesServed || []).filter((x: string) => !INDUSTRY_OPTIONS.includes(x)).join(', '),
        preferredContact: user.profileData?.preferredContact || '',
        callMeWhen: user.profileData?.callMeWhen || '',
        painPoint: user.profileData?.icp?.painPoint || '',
        geography: user.profileData?.icp?.geography || '',
        revenueMin: user.profileData?.icp?.revenueMin || '',
        revenueMax: user.profileData?.icp?.revenueMax || '',
        employeeCount: user.profileData?.icp?.employeeCount || '',
        seats: user.profileData?.icp?.seats || '',
    });

    const toggleIndustry = (name: string) => {
        setForm(prev => ({
            ...prev,
            industriesServed: prev.industriesServed.includes(name)
                ? prev.industriesServed.filter((x: string) => x !== name)
                : [...prev.industriesServed, name],
        }));
    };

    const appendText = (newText: string) => {
        setCrawledText(prev => prev ? prev + '\n\n' + newText : newText);
    };

    const handleCrawl = () => {
        const urls = websiteUrls.split('\n').map(u => u.trim()).filter(Boolean);
        if (!urls.length) {
            toast.error('Enter at least one URL');
            return;
        }
        setIsCrawling(true);
        ApiClient.post('api/profile/crawl', { urls }).then((res: any) => {
            setIsCrawling(false);
            if (res.success && res.data?.length) {
                const allText = res.data
                    .filter((r: any) => r.success)
                    .map((r: any) => r.text)
                    .join('\n\n');
                appendText(allText);
                const imported = res.data.filter((r: any) => r.success);
                setSources(prev => [...prev, ...imported.map((r: any) => `Website: ${r.url}`)]);
                setWebsiteUrls('');
                toast.success(`Imported ${imported.length} website${imported.length > 1 ? 's' : ''}. You can add more or continue.`);
            } else {
                toast.error('Could not crawl the website');
            }
        });
    };

    const handleImportLinkedIn = () => {
        if (!linkedInText.trim()) {
            toast.error('Paste your LinkedIn profile text');
            return;
        }
        if (linkedInText.trim() === importedLinkedIn.trim()) {
            toast.info('This LinkedIn text is already imported.');
            return;
        }
        appendText(linkedInText);
        setImportedLinkedIn(linkedInText);
        setSources(prev => prev.includes('LinkedIn profile text') ? prev : [...prev, 'LinkedIn profile text']);
        toast.success('Text added. You can paste more or continue.');
    };

    const handlePdfUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (!files || !files.length) return;

        const validFiles: File[] = [];
        for (let i = 0; i < files.length; i++) {
            const file = files[i];
            if (file.type !== 'application/pdf') {
                toast.error(`"${file.name}" is not a PDF — skipped`);
                continue;
            }
            if (file.size > 10 * 1024 * 1024) {
                toast.error(`"${file.name}" is too large (max 10 MB) — skipped`);
                continue;
            }
            validFiles.push(file);
        }
        if (!validFiles.length) return;

        setIsParsing(true);
        let parsed = 0;
        for (const file of validFiles) {
            const formData = new FormData();
            formData.append('file', file);
            try {
                const baseUrl = `${environment.api}api/profile/parse-pdf`;
                const resp = await fetch(baseUrl, { method: 'POST', body: formData });
                const res = await resp.json();
                if (res.success && res.data?.text) {
                    appendText(res.data.text);
                    setSources(prev => [...prev, `PDF: ${file.name}`]);
                    setPdfTexts(prev => [...prev, { name: file.name, text: res.data.text }]);
                    parsed++;
                } else {
                    toast.error(`Could not extract text from "${file.name}"`);
                }
            } catch {
                toast.error(`Failed to upload "${file.name}"`);
            }
        }
        setIsParsing(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
        e.target.value = '';
    };

    const handleGenerateWithAI = async () => {
        let pendingText = crawledText;
        let pendingSources = [...sources];

        // Auto-import any pending website URLs
        const urls = websiteUrls.split('\n').map(u => u.trim()).filter(Boolean);
        if (urls.length > 0) {
            setIsCrawling(true);
            try {
                const res = await new Promise<any>((resolve) => {
                    ApiClient.post('api/profile/crawl', { urls }).then(resolve);
                });
                setIsCrawling(false);
                if (res.success && res.data?.length) {
                    const imported = res.data.filter((r: any) => r.success);
                    const allText = imported.map((r: any) => r.text).join('\n\n');
                    if (allText) {
                        pendingText = pendingText ? pendingText + '\n\n' + allText : allText;
                        appendText(allText);
                        const newSources = imported.map((r: any) => `Website: ${r.url}`);
                        pendingSources = [...pendingSources, ...newSources];
                        setSources(prev => [...prev, ...newSources]);
                    }
                    setWebsiteUrls('');
                }
            } catch {
                setIsCrawling(false);
            }
        }

        // Auto-import LinkedIn text if it hasn't been imported yet (keep it in the box)
        if (linkedInText.trim() && linkedInText.trim() !== importedLinkedIn.trim()) {
            pendingText = pendingText ? pendingText + '\n\n' + linkedInText : linkedInText;
            appendText(linkedInText);
            if (!pendingSources.includes('LinkedIn profile text')) {
                pendingSources = [...pendingSources, 'LinkedIn profile text'];
            }
            setSources(prev => prev.includes('LinkedIn profile text') ? prev : [...prev, 'LinkedIn profile text']);
            setImportedLinkedIn(linkedInText);
        }

        if (!pendingText || pendingText.trim().length < 20) {
            setStep(2);
            return;
        }
        setIsGenerating(true);
        ApiClient.post('api/profile/generate-from-text', { text: pendingText }).then((res: any) => {
            setIsGenerating(false);
            if (res.success && res.data) {
                const d = res.data;
                setForm(prev => ({
                    ...prev,
                    headline: d.headline || prev.headline,
                    industry: d.industry || prev.industry,
                    specialties: Array.isArray(d.specialties) ? d.specialties.join(', ') : (d.specialties || prev.specialties),
                    servicesOffered: Array.isArray(d.servicesOffered) ? d.servicesOffered.join(', ') : (d.servicesOffered || prev.servicesOffered),
                    servicesNeeded: Array.isArray(d.servicesNeeded) ? d.servicesNeeded.join(', ') : (d.servicesNeeded || prev.servicesNeeded),
                    websiteUrl: d.websiteUrl || prev.websiteUrl,
                    yearsExperience: d.yearsExperience || prev.yearsExperience,
                    businessType: d.businessType || prev.businessType,
                    businessTypeOther: d.businessTypeOther || prev.businessTypeOther,
                    industriesServed: Array.isArray(d.industriesServed)
                        ? d.industriesServed.filter((x: string) => INDUSTRY_OPTIONS.includes(x))
                        : prev.industriesServed,
                    industryOther: Array.isArray(d.industriesServed)
                        ? (d.industriesServed.filter((x: string) => x && !INDUSTRY_OPTIONS.includes(x)).join(', ') || prev.industryOther)
                        : prev.industryOther,
                    callMeWhen: d.callMeWhen || prev.callMeWhen,
                    painPoint: d.painPoint || prev.painPoint,
                    geography: d.geography || prev.geography,
                    revenueMin: d.revenueMin || prev.revenueMin,
                    revenueMax: d.revenueMax || prev.revenueMax,
                    employeeCount: d.employeeCount || prev.employeeCount,
                    seats: d.seats || prev.seats,
                }));
                toast.success('Profile fields auto-filled. Review and edit as needed.');
            } else {
                toast.error('Could not auto-fill. Please fill in manually.');
            }
            setStep(2);
        }).catch(() => {
            setIsGenerating(false);
            toast.error('Could not auto-fill. Please fill in manually.');
            setStep(2);
        });
    };

    const handleRemovePdf = (fileName: string) => {
        const entry = pdfTexts.find(p => p.name === fileName);
        if (!entry) return;
        setPdfTexts(prev => prev.filter(p => p.name !== fileName));
        setSources(prev => prev.filter(s => s !== `PDF: ${fileName}`));
        setCrawledText(prev => {
            const parts = prev.split('\n\n');
            const entryParts = entry.text.split('\n\n');
            const filtered = parts.filter(p => !entryParts.includes(p));
            return filtered.join('\n\n');
        });
    };

    const handleRegenerate = () => {
        if (crawledText) {
            handleGenerateWithAI();
            toast.info('Re-analyzing with AI...');
        } else {
            setForm({
                headline: '',
                industry: '',
                specialties: '',
                servicesOffered: '',
                servicesNeeded: '',
                referralFee: '',
                promotions: '',
                websiteUrl: '',
                yearsExperience: '',
                businessType: '',
                businessTypeOther: '',
                industriesServed: [],
                industryOther: '',
                preferredContact: '',
                callMeWhen: '',
                painPoint: '',
                geography: '',
                revenueMin: '',
                revenueMax: '',
                employeeCount: '',
                seats: '',
            });
            setStep(1);
            toast.info('Profile form cleared. Re-import or fill in manually.');
        }
    };

    const handleSave = () => {
        const profileData = {
            headline: form.headline,
            industry: form.industry,
            specialties: form.specialties.split(',').map((s: string) => s.trim()).filter(Boolean),
            servicesOffered: form.servicesOffered.split(',').map((s: string) => s.trim()).filter(Boolean),
            servicesNeeded: form.servicesNeeded.split(',').map((s: string) => s.trim()).filter(Boolean),
            referralFee: form.referralFee,
            promotions: form.promotions,
            websiteUrl: form.websiteUrl,
            yearsExperience: form.yearsExperience ? Number(form.yearsExperience) : null,
            businessType: form.businessType,
            businessTypeOther: form.businessType === 'Other' ? form.businessTypeOther : '',
            industriesServed: [
                ...form.industriesServed,
                ...form.industryOther.split(',').map((s: string) => s.trim()).filter(Boolean),
            ],
            preferredContact: form.preferredContact,
            callMeWhen: form.callMeWhen,
            icp: {
                painPoint: form.painPoint,
                geography: form.geography,
                revenueMin: form.revenueMin,
                revenueMax: form.revenueMax,
                employeeCount: form.employeeCount,
                seats: form.seats,
            },
            profileImported: true,
            onboardingCompletedAt: new Date().toISOString(),
        };

        loader(true);
        ApiClient.put('api/user', {
            id: user._id,
            addedBy: user._id,
            profileData,
        }).then((res: any) => {
            loader(false);
            if (res.success) {
                dispatch(login_success({ ...user, profileData }));
                toast.success('Profile updated!');
                onClose();
                const activeGroup = user.groups?.find((g: any) => g.status === 'active');
                if (activeGroup?.groupId?._id || activeGroup?.groupId) {
                    const gId = activeGroup.groupId._id || activeGroup.groupId;
                    ApiClient.post('api/profile/generate-embedding', { userId: user._id, groupId: gId }).catch(() => {});
                }
            } else {
                toast.error('Failed to save profile');
            }
        });
    };

    const handleSkip = () => {
        const updatedProfileData = {
            ...user.profileData,
            onboardingSkippedAt: new Date().toISOString(),
        };
        dispatch(login_success({ ...user, profileData: updatedProfileData }));
        ApiClient.put('api/user', {
            id: user._id,
            addedBy: user._id,
            profileData: updatedProfileData,
        }).catch(() => {});
        onClose();
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[9999] flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                <div className="p-6">
                    <div className="flex items-center justify-between mb-6">
                        <h2 className="text-xl font-bold">Complete Your Profile</h2>
                        <button onClick={handleSkip} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
                    </div>

                    <div className="flex items-center gap-2 mb-6">
                        {[1, 2, 3].map(s => (
                            <div key={s} className={`h-1.5 flex-1 rounded-full ${step >= s ? 'bg-orange-500' : 'bg-gray-200'}`} />
                        ))}
                    </div>

                    {step === 1 && (
                        <div>
                            <p className="text-gray-600 mb-4">
                                Help other members find you by importing your professional profile. Choose one or more options below, or skip and fill in manually.
                            </p>

                            {sources.length > 0 && (
                                <div className="p-3 mb-4 bg-green-50 border border-green-200 rounded-lg">
                                    <p className="text-xs font-medium text-green-700 mb-1">Imported {sources.length} source{sources.length > 1 ? 's' : ''} — AI will analyze all of them:</p>
                                    <ul className="text-xs text-green-600 list-disc pl-4">
                                        {sources.map((s, i) => <li key={i}>{s}</li>)}
                                    </ul>
                                </div>
                            )}

                            <div className="space-y-3 mb-6">
                                {/* Option 1: Import from Website(s) */}
                                <div className={`rounded-lg border-2 transition ${websiteOpen ? 'border-orange-500 bg-orange-50' : 'border-gray-200'}`}>
                                    <button
                                        type="button"
                                        onClick={() => { setWebsiteOpen(!websiteOpen); setLinkedInOpen(false); setPdfOpen(false); }}
                                        className="w-full text-left p-4"
                                    >
                                        <div className="font-medium">{sources.some(s => s.startsWith('Website:')) ? '✓ ' : ''}Import from Website(s)</div>
                                        <div className="text-sm text-gray-500">We'll extract business information from your website</div>
                                    </button>
                                    {websiteOpen && (
                                        <div className="px-4 pb-4">
                                            <label className="text-sm font-medium block mb-1">Website URL(s) — one per line</label>
                                            <textarea
                                                className="shadow-box bg-white w-full rounded-lg p-2 text-sm h-20"
                                                placeholder={"https://yourwebsite.com\nhttps://yourcompany.com/about"}
                                                value={websiteUrls}
                                                onChange={e => setWebsiteUrls(e.target.value)}
                                            />
                                            <button
                                                type="button"
                                                onClick={handleCrawl}
                                                disabled={isCrawling}
                                                className="text-white bg-orange-500 hover:bg-orange-600 font-medium rounded-lg text-sm px-4 py-2 mt-2"
                                            >
                                                {isCrawling ? 'Crawling...' : 'Import'}
                                            </button>
                                        </div>
                                    )}
                                </div>

                                {/* Option 2: Paste LinkedIn Profile */}
                                <div className={`rounded-lg border-2 transition ${linkedInOpen ? 'border-orange-500 bg-orange-50' : 'border-gray-200'}`}>
                                    <button
                                        type="button"
                                        onClick={() => { setLinkedInOpen(!linkedInOpen); setWebsiteOpen(false); setPdfOpen(false); }}
                                        className="w-full text-left p-4"
                                    >
                                        <div className="font-medium">{sources.some(s => s.startsWith('LinkedIn')) ? '✓ ' : ''}Paste LinkedIn Profile</div>
                                        <div className="text-sm text-gray-500">Copy-paste your LinkedIn text, or upload your LinkedIn PDF export</div>
                                    </button>
                                    {linkedInOpen && (
                                        <div className="px-4 pb-4">
                                            <p className="text-xs text-gray-400 mb-3">
                                                Go to your LinkedIn profile, select and copy text from your About, Experience, and Skills sections and paste below. Or export your profile as PDF (LinkedIn → your profile → "More" → "Save to PDF") and upload it.
                                            </p>
                                            <textarea
                                                className="shadow-box bg-white w-full rounded-lg p-2 text-sm h-28"
                                                placeholder="Paste your LinkedIn profile text here..."
                                                value={linkedInText}
                                                onChange={e => setLinkedInText(e.target.value)}
                                            />
                                            <div className="flex items-center gap-3 mt-2">
                                                <button
                                                    type="button"
                                                    onClick={handleImportLinkedIn}
                                                    className="text-white bg-orange-500 hover:bg-orange-600 font-medium rounded-lg text-sm px-4 py-2"
                                                >
                                                    Use Pasted Text
                                                </button>
                                                <span className="text-xs text-gray-400">or</span>
                                                <label className="cursor-pointer text-sm font-medium text-orange-600 hover:text-orange-700 bg-orange-50 px-4 py-2 rounded-lg">
                                                    Upload LinkedIn PDF
                                                    <input
                                                        ref={fileInputRef}
                                                        type="file"
                                                        accept=".pdf"
                                                        multiple
                                                        onChange={handlePdfUpload}
                                                        className="hidden"
                                                    />
                                                </label>
                                            </div>
                                            {isParsing && <p className="text-sm text-orange-500 mt-2">Extracting text from PDF...</p>}
                                        </div>
                                    )}
                                </div>

                                {/* Option 3: Upload Relevant PDFs */}
                                <div className={`rounded-lg border-2 transition ${pdfOpen ? 'border-orange-500 bg-orange-50' : 'border-gray-200'}`}>
                                    <button
                                        type="button"
                                        onClick={() => { setPdfOpen(!pdfOpen); setWebsiteOpen(false); setLinkedInOpen(false); }}
                                        className="w-full text-left p-4"
                                    >
                                        <div className="font-medium">{sources.some(s => s.startsWith('PDF:')) ? '✓ ' : ''}Upload Relevant PDFs</div>
                                        <div className="text-sm text-gray-500">Upload a professional bio, capabilities brochure, one-pager, or a document that describes your services</div>
                                    </button>
                                    {pdfOpen && (
                                        <div className="px-4 pb-4">
                                            <p className="text-xs text-gray-400 mb-2">
                                                Upload any PDF that describes you or your business — resume, capability statement, services brochure, etc.
                                            </p>
                                            <input
                                                type="file"
                                                accept=".pdf"
                                                multiple
                                                onChange={handlePdfUpload}
                                                className="block w-full text-sm text-gray-500 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-orange-50 file:text-orange-700 hover:file:bg-orange-100"
                                            />
                                            {isParsing && <p className="text-sm text-orange-500 mt-2">Extracting text from PDF...</p>}
                                            {pdfTexts.length > 0 && (
                                                <div className="mt-2 text-xs text-gray-600 space-y-1">
                                                    {pdfTexts.map((p, i) => (
                                                        <div key={i} className="flex items-center justify-between">
                                                            <span className="flex items-center gap-1"><span className="text-green-600">✓</span> {p.name}</span>
                                                            <button type="button" onClick={() => handleRemovePdf(p.name)} className="text-red-400 hover:text-red-600 text-xs ml-2">Remove</button>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>

                            </div>

                            <div className="flex justify-between mt-6">
                                <button type="button" onClick={handleSkip} className="text-gray-500 hover:text-gray-700 text-sm">
                                    Skip for now
                                </button>
                                <button
                                    type="button"
                                    onClick={
                                        (sources.length > 0 || websiteUrls.trim() || linkedInText.trim())
                                            ? handleGenerateWithAI
                                            : () => setStep(2)
                                    }
                                    disabled={isGenerating || isCrawling}
                                    className="text-white bg-orange-500 hover:bg-orange-600 font-medium rounded-lg text-sm px-6 py-2 disabled:opacity-50"
                                >
                                    {isCrawling ? 'Importing websites...' : isGenerating ? 'Analyzing with AI...' : (sources.length > 0 || websiteUrls.trim() || linkedInText.trim()) ? 'Next' : 'Fill in Manually'}
                                </button>
                            </div>
                        </div>
                    )}

                    {step === 2 && (
                        <div>
                            <p className="text-gray-600 mb-4 text-sm">
                                {crawledText
                                    ? 'We used AI to pre-fill these fields from your imported text. Review and edit as needed.'
                                    : 'Fill in the details that help others find and refer you.'}
                            </p>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div>
                                    <label className="text-sm font-medium block mb-1">Professional Headline</label>
                                    <input
                                        type="text"
                                        className="shadow-box bg-white w-full rounded-lg h-10 px-2 text-sm"
                                        placeholder="e.g. Full-stack developer | AI specialist"
                                        value={form.headline}
                                        onChange={e => setForm({ ...form, headline: e.target.value })}
                                    />
                                </div>
                                <div>
                                    <label className="text-sm font-medium block mb-1">Industry</label>
                                    <input
                                        type="text"
                                        className="shadow-box bg-white w-full rounded-lg h-10 px-2 text-sm"
                                        placeholder="e.g. Technology, Real Estate"
                                        value={form.industry}
                                        onChange={e => setForm({ ...form, industry: e.target.value })}
                                    />
                                </div>
                                <div className="md:col-span-2">
                                    <label className="text-sm font-medium block mb-1">Specialties <span className="text-xs text-gray-400">(comma-separated)</span></label>
                                    <input
                                        type="text"
                                        className="shadow-box bg-white w-full rounded-lg h-10 px-2 text-sm"
                                        placeholder="e.g. computer vision, React, AWS"
                                        value={form.specialties}
                                        onChange={e => setForm({ ...form, specialties: e.target.value })}
                                    />
                                </div>
                                <div>
                                    <label className="text-sm font-medium block mb-1">Services Offered <span className="text-xs text-gray-400">(comma-separated)</span></label>
                                    <input
                                        type="text"
                                        className="shadow-box bg-white w-full rounded-lg h-10 px-2 text-sm"
                                        placeholder="e.g. web dev, consulting"
                                        value={form.servicesOffered}
                                        onChange={e => setForm({ ...form, servicesOffered: e.target.value })}
                                    />
                                </div>
                                <div>
                                    <label className="text-sm font-medium block mb-1">Services Needed <span className="text-xs text-gray-400">(comma-separated)</span></label>
                                    <input
                                        type="text"
                                        className="shadow-box bg-white w-full rounded-lg h-10 px-2 text-sm"
                                        placeholder="e.g. bookkeeping, marketing"
                                        value={form.servicesNeeded}
                                        onChange={e => setForm({ ...form, servicesNeeded: e.target.value })}
                                    />
                                </div>
                                <div>
                                    <label className="text-sm font-medium block mb-1">Website URL</label>
                                    <input
                                        type="text"
                                        className="shadow-box bg-white w-full rounded-lg h-10 px-2 text-sm"
                                        placeholder="https://yoursite.com"
                                        value={form.websiteUrl}
                                        onChange={e => setForm({ ...form, websiteUrl: e.target.value })}
                                    />
                                </div>
                                <div>
                                    <label className="text-sm font-medium block mb-1">Years of Experience</label>
                                    <input
                                        type="number"
                                        className="shadow-box bg-white w-full rounded-lg h-10 px-2 text-sm"
                                        value={form.yearsExperience}
                                        onChange={e => setForm({ ...form, yearsExperience: e.target.value })}
                                    />
                                </div>
                                <div>
                                    <label className="text-sm font-medium block mb-1">Customer Type</label>
                                    <select
                                        className="shadow-box bg-white w-full rounded-lg h-10 px-2 text-sm"
                                        value={form.businessType}
                                        onChange={e => setForm({ ...form, businessType: e.target.value })}
                                    >
                                        <option value="">Select...</option>
                                        <option value="B2B">B2B</option>
                                        <option value="B2C">B2C</option>
                                        <option value="Nonprofit">Nonprofit</option>
                                        <option value="Other">Other</option>
                                    </select>
                                    {form.businessType === 'Other' && (
                                        <input
                                            type="text"
                                            className="shadow-box bg-white w-full rounded-lg h-10 px-2 text-sm mt-2"
                                            placeholder="Describe your customer type"
                                            value={form.businessTypeOther}
                                            onChange={e => setForm({ ...form, businessTypeOther: e.target.value })}
                                        />
                                    )}
                                </div>
                                <div>
                                    <label className="text-sm font-medium block mb-1">Preferred Contact</label>
                                    <select
                                        className="shadow-box bg-white w-full rounded-lg h-10 px-2 text-sm"
                                        value={form.preferredContact}
                                        onChange={e => setForm({ ...form, preferredContact: e.target.value })}
                                    >
                                        <option value="">Select...</option>
                                        <option value="email">Email</option>
                                        <option value="phone">Phone</option>
                                        <option value="linkedin">LinkedIn</option>
                                    </select>
                                </div>
                            </div>

                            <div className="mt-4">
                                <label className="text-sm font-medium block mb-1">Which industries do you serve</label>
                                <div className="flex flex-wrap gap-2">
                                    {INDUSTRY_OPTIONS.map(name => {
                                        const active = form.industriesServed.includes(name);
                                        return (
                                            <button
                                                type="button"
                                                key={name}
                                                onClick={() => toggleIndustry(name)}
                                                className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                                                    active
                                                        ? 'bg-orange-500 border-orange-500 text-white'
                                                        : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                                                }`}
                                            >
                                                {name}
                                            </button>
                                        );
                                    })}
                                </div>
                                <div className="mt-2">
                                    <label className="text-xs text-gray-500 block mb-1">Other (not listed) <span className="text-gray-400">— comma-separated</span></label>
                                    <input
                                        type="text"
                                        className="shadow-box bg-white w-full rounded-lg h-10 px-2 text-sm"
                                        placeholder="e.g. Agriculture, Nonprofit Services"
                                        value={form.industryOther}
                                        onChange={e => setForm({ ...form, industryOther: e.target.value })}
                                    />
                                </div>
                            </div>

                            {crawledText && (
                                <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                                    <p className="text-xs font-medium text-blue-700 mb-1">Imported text from {sources.length} source{sources.length > 1 ? 's' : ''} (reference):</p>
                                    <div className="max-h-40 overflow-y-auto text-xs text-blue-600 whitespace-pre-wrap">
                                        {crawledText}
                                    </div>
                                </div>
                            )}

                            <div className="flex justify-between mt-6">
                                <div className="flex gap-3">
                                    <button type="button" onClick={() => setStep(1)} className="text-gray-500 hover:text-gray-700 text-sm">
                                        Back
                                    </button>
                                    <button type="button" onClick={handleRegenerate} className="text-orange-500 hover:text-orange-700 text-sm">
                                        Start Over
                                    </button>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setStep(3)}
                                    className="text-white bg-orange-500 hover:bg-orange-600 font-medium rounded-lg text-sm px-6 py-2"
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    )}

                    {step === 3 && (
                        <div>
                            <p className="text-gray-600 mb-4 text-sm">
                                Help others know when to refer you. This is optional but makes you much more findable.
                            </p>

                            <div className="grid grid-cols-1 gap-3">
                                <div>
                                    <label className="text-sm font-medium block mb-1">"Call me when..." <span className="text-xs text-gray-400">(your ideal referral)</span></label>
                                    <input
                                        type="text"
                                        className="shadow-box bg-white w-full rounded-lg h-10 px-2 text-sm"
                                        placeholder="e.g. You know someone who needs a new website for their small business"
                                        value={form.callMeWhen}
                                        onChange={e => setForm({ ...form, callMeWhen: e.target.value })}
                                    />
                                </div>
                                <div>
                                    <label className="text-sm font-medium block mb-1">Customer Pain Point</label>
                                    <input
                                        type="text"
                                        className="shadow-box bg-white w-full rounded-lg h-10 px-2 text-sm"
                                        placeholder="e.g. struggling with outdated technology"
                                        value={form.painPoint}
                                        onChange={e => setForm({ ...form, painPoint: e.target.value })}
                                    />
                                </div>
                                <div>
                                    <label className="text-sm font-medium block mb-1">Target Geography</label>
                                    <input
                                        type="text"
                                        className="shadow-box bg-white w-full rounded-lg h-10 px-2 text-sm"
                                        placeholder="e.g. San Diego, Southern California"
                                        value={form.geography}
                                        onChange={e => setForm({ ...form, geography: e.target.value })}
                                    />
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                    <div>
                                        <label className="text-sm font-medium block mb-1">Revenue <span className="text-xs text-gray-400">(enter a range minimum–maximum)</span></label>
                                        <div className="flex items-center gap-2">
                                            <input
                                                type="text"
                                                className="shadow-box bg-white w-full rounded-lg h-10 px-2 text-sm"
                                                placeholder="Min"
                                                value={form.revenueMin}
                                                onChange={e => setForm({ ...form, revenueMin: e.target.value })}
                                            />
                                            <span className="text-gray-400">–</span>
                                            <input
                                                type="text"
                                                className="shadow-box bg-white w-full rounded-lg h-10 px-2 text-sm"
                                                placeholder="Max"
                                                value={form.revenueMax}
                                                onChange={e => setForm({ ...form, revenueMax: e.target.value })}
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="text-sm font-medium block mb-1">Number of Employees</label>
                                        <input
                                            type="number"
                                            className="shadow-box bg-white w-full rounded-lg h-10 px-2 text-sm"
                                            placeholder="e.g. 50"
                                            value={form.employeeCount}
                                            onChange={e => setForm({ ...form, employeeCount: e.target.value })}
                                        />
                                    </div>
                                    <div>
                                        <label className="text-sm font-medium block mb-1">Number of Seats</label>
                                        <input
                                            type="number"
                                            className="shadow-box bg-white w-full rounded-lg h-10 px-2 text-sm"
                                            placeholder="e.g. 25"
                                            value={form.seats}
                                            onChange={e => setForm({ ...form, seats: e.target.value })}
                                        />
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="text-sm font-medium block mb-1">Referral Fee</label>
                                        <input
                                            type="text"
                                            className="shadow-box bg-white w-full rounded-lg h-10 px-2 text-sm"
                                            placeholder="e.g. 10% for first 6 months"
                                            value={form.referralFee}
                                            onChange={e => setForm({ ...form, referralFee: e.target.value })}
                                        />
                                    </div>
                                    <div>
                                        <label className="text-sm font-medium block mb-1">Current Promotions</label>
                                        <input
                                            type="text"
                                            className="shadow-box bg-white w-full rounded-lg h-10 px-2 text-sm"
                                            placeholder="e.g. Free consult through Dec"
                                            value={form.promotions}
                                            onChange={e => setForm({ ...form, promotions: e.target.value })}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="flex justify-between mt-6">
                                <div className="flex gap-3">
                                    <button type="button" onClick={() => setStep(2)} className="text-gray-500 hover:text-gray-700 text-sm">
                                        Back
                                    </button>
                                    <button type="button" onClick={handleRegenerate} className="text-orange-500 hover:text-orange-700 text-sm">
                                        Start Over
                                    </button>
                                </div>
                                <button
                                    type="button"
                                    onClick={handleSave}
                                    className="text-white bg-orange-500 hover:bg-orange-600 font-medium rounded-lg text-sm px-6 py-2"
                                >
                                    Save Profile
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default OnboardingWizard;
